import { useEffect, useRef, useState } from "react";
import type { AuthedRequester } from "@/lib/brokerAuth";
import {
  isAccountingLinkSettled,
  isBankLinkSettled,
  watchAccountingLink,
  watchBankLink,
  type BondAccountingLink,
  type BondBankLink,
  type BondLinkWatchOptions,
} from "@/lib/brokerBonds";

// BRK-S119, generalised to BOTH customer-connection links in BRK-S121 — the
// freshest link state for a surface that was handed a seed and has no other
// reason to re-read (NN #9: no client-side persistence, the server is the
// source).
//
// Lives in lib, like `usePageTitle` and `useExtractedReview`, so the page file
// stays components-only for react-refresh — and so the lifecycle is testable
// without an AuthProvider: the requester is an ARGUMENT here, not a context
// read, which is what lets a test script it.
//
// ONE HOOK, TWO CARDS, which is why the file is named for the pair rather than
// for the accounting link it started as. What differs between them is which
// watcher to start and whether the state in hand can still move — two functions,
// declared once per card below. The seeding rule, the prop-adoption rule and the
// teardown are identical, and none of the three is obvious enough to be worth
// writing twice.

/** Timing knobs, mirroring `PollOptions`: injected in tests, real otherwise. */
export interface BondLinkPollOptions {
  intervalMs?: number;
  wait?: (ms: number) => Promise<void>;
}

/** What tells one card's refresh from the other's, and NOTHING else. Declared
 *  at module scope so both are referentially stable: the effect below takes one
 *  as a dependency, and a per-render object would restart the watch on every
 *  render of the surface. */
interface BondLinkKind<L> {
  /**
   * Whether the link in hand can still change on its own.
   *
   * FALSE for a settled state, and — on the bank card — false for a state that
   * is UNKNOWN. Those are different facts with the same answer: nothing is
   * known about a pre-5.5.0 order's progress, and nothing will become known by
   * asking the server that has no state to give.
   */
  readonly moves: (link: L) => boolean;
  readonly watch: (
    request: AuthedRequester,
    orderId: string,
    options: BondLinkWatchOptions<L>,
  ) => () => void;
}

const ACCOUNTING: BondLinkKind<BondAccountingLink> = {
  // `unminted`, `pending`, `linked` and `syncing` all move on their own; the
  // other three wait on a person (BRK-S119).
  moves: (link) => !isAccountingLinkSettled(link.state),
  watch: watchAccountingLink,
};

const BANK: BondLinkKind<BondBankLink> = {
  // `connected` DOES move, deliberately: the figures fetch is a separate
  // orchestration upstream, so it is the state a broker most wants to see
  // change. `figuresReady` is the only settled one, and an absent state is not
  // a state at all (BRK-S121).
  moves: (link) => link.state !== undefined && !isBankLinkSettled(link.state),
  watch: watchBankLink,
};

/**
 * The link this surface should render, refreshed while its state can still move
 * on its own.
 *
 * WHY A HOOK AND NOT A REFETCH OF THE PAGE. Both surfaces that show these cards
 * got their order from a response that is otherwise complete: the confirmation
 * screen reads the submit's OWN body (BRK-S115 — no refetch, by design) and the
 * filed record has already done its read. Re-fetching the whole page on a timer
 * to move one card would re-render a record the broker may be reading, and on
 * the confirmation screen it would re-derive a screen that has no read at all.
 * So the CARD owns its own freshness, seeded by whatever its surface was given.
 *
 * SEEDED, NEVER CONTRADICTED. The seed is adopted whenever the caller hands
 * down a different link — a resumed order, or a surface that re-read for its own
 * reasons — and otherwise the polled state wins, because it is strictly newer
 * than the prop that seeded it.
 *
 * An `undefined` link stays undefined and starts nothing: the key is absent when
 * there is nothing to hand over, which is a fact about the order and never
 * becomes true later. Same for a missing `orderId`: with no id there is nothing
 * to re-read, so the card renders the state it was handed.
 */
function useBondLinkState<L>(
  request: AuthedRequester,
  orderId: string | undefined,
  link: L | undefined,
  kind: BondLinkKind<L>,
  options: BondLinkPollOptions,
): L | undefined {
  const { intervalMs, wait } = options;
  const [current, setCurrent] = useState<L | undefined>(link);
  // Adjusting state when a prop changes, React's documented way: the seed is
  // whatever the surface holds, and a NEW seed replaces a polled state rather
  // than being ignored. Comparing the prop to the last PROP (not to `current`)
  // is what keeps a poll's own update from being reverted on the next render.
  const seed = useRef(link);
  if (seed.current !== link) {
    seed.current = link;
    setCurrent(link);
  }

  // The VERDICT is the dependency, not the link and not its state: a move
  // between two still-moving states leaves the loop that is already running
  // alone, and reaching a settled one tears it down through this effect's own
  // cleanup rather than relying only on the loop noticing. Unmount cancels it
  // either way.
  const moving = current !== undefined && kind.moves(current);
  useEffect(() => {
    // Nothing to watch: no order to re-read, no link on the order, or a state
    // that cannot move. That last one is the stop condition — a settled card
    // must not keep a request loop alive behind it.
    if (orderId === undefined || !moving) return;
    return kind.watch(request, orderId, {
      ...(intervalMs === undefined ? {} : { intervalMs }),
      ...(wait === undefined ? {} : { wait }),
      onUpdate: setCurrent,
    });
  }, [request, orderId, kind, moving, intervalMs, wait]);

  return current;
}

/** The ACCOUNTING link to render, refreshed while its state can still move. */
export function useAccountingLinkState(
  request: AuthedRequester,
  orderId: string | undefined,
  link: BondAccountingLink | undefined,
  options: BondLinkPollOptions = {},
): BondAccountingLink | undefined {
  return useBondLinkState(request, orderId, link, ACCOUNTING, options);
}

/**
 * The BANK link to render, refreshed while its state can still move.
 *
 * TWO REASONS NOT TO POLL, and they are different facts (BRK-S121):
 *
 *   • `figuresReady` — settled. The figures are here and nothing moves again.
 *   • NO `state` at all — an order minted before contract 5.5.0. The card
 *     renders the link with no status line and starts no loop, which is exactly
 *     what it did before this story.
 */
export function useBankLinkState(
  request: AuthedRequester,
  orderId: string | undefined,
  link: BondBankLink | undefined,
  options: BondLinkPollOptions = {},
): BondBankLink | undefined {
  return useBondLinkState(request, orderId, link, BANK, options);
}
